import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { updateState } from "../counter/counterSlice";
import { ModernWebLayout, PageSidebar, MainContent } from "../../components/Layout";
import ResourceModal from "../../components/resources/ResourceModal";
import { ModernBreadcrumb } from "../../components/Breadcrumbs";
import useResourceModal from "../../hooks/modalHooks/useResourceModal";
import { RESOURCE_MENU_ITEMS } from "../../constants/navigation";
import PatientInfoApp from "../patientInfo/PatientInfoApp";
import styles from "./ModernResources.module.scss";
import ModernTablesAndFigures from "../tables-and-figures/webmode/ModernTablesAndFigures";
import AcknowledgementContent from "../guideline/overview/AcknowledgementContent";
import ExpertGroupContent from "../guideline/overview/ExpertGroupContent";

const ModernResources = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPath = location.pathname.split("/")[2] || "";
  const { modalState, shouldUseModal, openResource, closeModal } = useResourceModal();
  const resourceContentRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Hide sidebar when a specific resource is selected, show on main page
  const showSidebar = !currentPath;

  useEffect(() => {
    dispatch(
      updateState({
        ditaPath: "",
        headTitle: "",
      }),
    );
  }, [dispatch]);

  // Get resource menu items from centralized config
  const resourceMenuItems = RESOURCE_MENU_ITEMS;

  // Create menu items with modal-aware click handlers
  // Only use modal behavior when NOT showing PageSidebar (i.e., when accessed from app sidebar)
  // Skip adding onClick handler for external links - let PageSidebar handle them
  const createMenuItemsWithModalHandlers = (items) => {
    const useModalForItems = shouldUseModal && !showSidebar;
    return items.map((item) => ({
      ...item,
      onClick:
        useModalForItems && !item.isExternal
          ? (e) => {
              e.preventDefault();
              openResource(item.id, navigate);
            }
          : undefined,
    }));
  };

  const resourceMenuItemsWithHandlers = createMenuItemsWithModalHandlers(resourceMenuItems);

  // Determine if sidebar should be closable
  const isOnResourcePages = location.pathname.startsWith("/resources");
  const isClosableSidebar = shouldUseModal && !isOnResourcePages;

  const pageSidebarComponent = (
    <PageSidebar
      title="Resources"
      icon="tg-icons-Resources"
      menuItems={resourceMenuItemsWithHandlers}
      defaultActiveId={currentPath || null}
      isClosable={isClosableSidebar}
      onClose={isClosableSidebar ? () => setSidebarOpen(false) : null}
    />
  );

  return (
    <ModernWebLayout
      title="Resources - Therapeutic Guidelines"
      pageSidebar={showSidebar && sidebarOpen ? pageSidebarComponent : null}
    >
      <MainContent className={styles.resourceContent} padding="default">
        {/* Show breadcrumb only for pregnancy-breastfeeding resource */}
        {currentPath === "pregnancy-breastfeeding" && (
          <ModernBreadcrumb
            customPath={[
              { label: "Guidelines", path: "/guidelines", isClickable: true },
              {
                label: "Pregnancy and breastfeeding",
                path: null,
                isClickable: false,
              },
            ]}
          />
        )}
        <div ref={resourceContentRef}>
          {/* Show specific resources when selected */}
          {currentPath === "patient-info" && <PatientInfoApp data-testid="patient-info" />}
          {currentPath === "antibiotic-summary" && (
            <div data-testid="antibiotic-summary">
              <h1>Antibiotic prescribing in primary care - summary table</h1>
            </div>
          )}
          {currentPath === "surgical-prophylaxis" && (
            <div data-testid="surgical-prophylaxis">
              <h1>Surgical prophylaxis - summary tables</h1>
            </div>
          )}
          {currentPath === "pregnancy-breastfeeding" && (
            <div data-testid="pregnancy-breastfeeding">
              <h1>Pregnancy and breastfeeding</h1>
            </div>
          )}
          {currentPath === "tables-and-figures" && <ModernTablesAndFigures />}
          {location.pathname.includes("expert-groups") && (
            <ExpertGroupContent isOpen={true} showCollapseButton={false} />
          )}
          {location.pathname.includes("acknowledgements") && (
            <AcknowledgementContent
              isOpen={true}
              showCollapseButton={false}
              showExtraInfo={false}
            />
          )}
          {!currentPath && (
            <div className={styles.resourceLanding}>
              {/* Empty landing page - similar to calculators */}
            </div>
          )}
        </div>
      </MainContent>

      {/* Resource Modal */}
      <ResourceModal
        resourceId={modalState.resourceId}
        isOpen={modalState.isOpen}
        onClose={closeModal}
      />
    </ModernWebLayout>
  );
};

export default ModernResources;
